import { useEffect, useRef } from 'react';

type UseDocumentEventOptions = {
  /** Pass false to stay unsubscribed, e.g. until a condition is met. */
  enabled?: boolean;
};

/**
 * Subscribes to a `document` event for the lifetime of the component — the way
 * the host page and the SPA talk to each other (see `CustomEvents`). The latest
 * handler is always the one called, so it does not have to be memoized.
 */
export const useDocumentEvent = (
  eventName: string,
  handler: (event: Event) => void,
  { enabled = true }: UseDocumentEventOptions = {}
) => {
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const listener = (event: Event) => handlerRef.current(event);

    document.addEventListener(eventName, listener);

    return () => {
      document.removeEventListener(eventName, listener);
    };
  }, [enabled, eventName]);
};
